import { CSSProperties, memo, MutableRefObject, useEffect, useLayoutEffect, useRef } from 'react'
import EmptySVGPlaceholder from './EmptySVGPlaceholder'
import svgMethods from './svg.methods'
import svgConditions from './svg.conditions'
import svgAnimations, { AnimationsState } from './svg.animations'
import animationsCss from './svgAnimations.module.css'
import { useRecoilValue } from 'recoil'
import { ISVGChartWidget } from '../../dashboards.types.ts'
import useHttpLoader from '../../../../shared/hooks/useHttpLoader.ts'
import { HTTPResponse } from '@/services/http/http.types.ts'
import parametersSelectors from '../../../Parameters/parameters.selectors.ts'
import parametersUtils from '../../../Parameters/parameters.utils.ts'
import { IParameter } from '../../../Parameters/parameters.types.ts'
import libraryDocumentsApi from '@/pages/Library/libraryDocuments.api.ts'
import { TLastValuesDataRef } from '@/services/data/data.types.ts'

interface IProps {
  widget: ISVGChartWidget
  lastResize?: number

  // For local svg files
  svgFileSrcString?: string
  textParameterPreprocessFn?: (parameter: IParameter, value: string) => string
  dataRef: MutableRefObject<TLastValuesDataRef>
  wrapperStyle?: CSSProperties
}

const SVGChartContent = (props: IProps) => {
  const { wait, loading } = useHttpLoader()
  const wrapperRef = useRef<HTMLDivElement>()
  const srcLoaded = useRef(false)

  const parametersMap = useRecoilValue(parametersSelectors.getParametersMap)
  const unitsMap = useRecoilValue(parametersSelectors.getUnitsMap)
  const unitPrefixesMap = useRecoilValue(parametersSelectors.getUnitsPrefixesMap)

  // Загрузить src svg-изображения и поместить его в контейнер
  useLayoutEffect(() => {
    const job = async () => {
      let imageSrc: HTTPResponse<string>
      if (!props.svgFileSrcString) {
        imageSrc = await libraryDocumentsApi.rawDocument(props.widget.configuration.settings.image_id, 'widget.svg')
      } else {
        imageSrc = { status: 'success', body: props.svgFileSrcString }
      }

      if (imageSrc.status === 'success') {
        const rect = wrapperRef.current.getBoundingClientRect()

        wrapperRef.current.innerHTML = imageSrc.body
        const svgEl = wrapperRef.current.querySelector('svg')

        svgMethods.fitSVG(svgEl, rect.width, rect.height)
        srcLoaded.current = true
      }
    }

    wait(job())
  }, [props.widget.configuration.settings.image_id])

  useEffect(() => {
    const callback = () => {
      const rect = wrapperRef.current.getBoundingClientRect()
      const svgEl = wrapperRef.current.querySelector('svg')
      if (!svgEl) return

      svgMethods.fitSVG(svgEl, rect.width, rect.height)
    }

    const observer = new ResizeObserver(callback)
    observer.observe(wrapperRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!srcLoaded.current) return

    let prevRender = null
    const animationsState: AnimationsState = {}
    let rafId: number

    const svgElement = wrapperRef.current.querySelector('svg')
    if (svgElement) {
      svgAnimations.prepareSVG(props.widget.configuration.settings.movements, svgElement)
      svgAnimations.prepareSVG(props.widget.configuration.settings.rotations, svgElement)
    }

    const render = (ts: number) => {
      if (!wrapperRef.current) return

      if (prevRender === null) {
        prevRender = ts
      } else if (ts - prevRender < 400) {
        rafId = requestAnimationFrame(render)
        return
      } else {
        prevRender = ts
      }

      const svgEl = wrapperRef.current.querySelector('svg')
      if (!svgEl) {
        rafId = requestAnimationFrame(render)
        return
      }

      const conditionsResult = svgConditions.computeMatches(props.widget.configuration.settings, props.dataRef.current)

      for (const colorResult of conditionsResult.color) {
        const node: SVGElement = svgEl.querySelector('#' + colorResult.node_id)
        if (!node) continue

        if (colorResult.isMatch) {
          svgMethods.applyColor(node, colorResult.color)
        } else {
          svgMethods.applyColor(node, null)
        }
      }

      for (const rotationResult of conditionsResult.rotations) {
        const node: SVGElement = svgEl.querySelector('#' + rotationResult.node_id)
        if (!node) continue

        if (rotationResult.isMatch) {
          animationsState[rotationResult.node_id] = svgAnimations.animate(rotationResult, svgEl)
        } else {
          window.clearInterval(+animationsState[rotationResult.node_id])
          animationsState[rotationResult.node_id] = false
          svgAnimations.cancelAnimations([rotationResult], svgEl)
        }
      }

      for (const movementResult of conditionsResult.movements) {
        const node: SVGElement = svgEl.querySelector('#' + movementResult.node_id)
        if (!node) continue

        if (movementResult.isMatch) {
          animationsState[movementResult.node_id] = svgAnimations.animate(movementResult, svgEl)
        } else {
          window.clearInterval(+animationsState[movementResult.node_id])
          animationsState[movementResult.node_id] = false
          svgAnimations.cancelAnimations([movementResult], svgEl)
        }
      }

      for (const visibilityResult of conditionsResult.visibility) {
        const node: SVGElement = svgEl.querySelector('#' + visibilityResult.node_id)
        if (!node) continue
        if (visibilityResult.isMatch) {
          switch (visibilityResult.action) {
            case 'blink':
              node.classList.add(animationsCss.blinking)
              break
            case 'hide':
              node.style.display = 'none'
              break
            case 'show':
              node.style.display = 'inline'
              break
          }
        } else {
          node.style.display = ''
          node.classList.remove(animationsCss.blinking)
        }
      }

      for (const textRow of props.widget.configuration.settings.text) {
        const node: SVGElement = svgEl.querySelector('#' + textRow.node_id)
        if (!node) continue

        const value = props.dataRef.current?.[textRow.device_id]?.[textRow.parameter_id]
        if (value === undefined || value === null) continue

        const parameter = parametersMap[textRow.parameter_id]
        const formattedValue = parametersUtils.formatParameterValue({ parameter, value, unitsMap, unitPrefixesMap })
        let visibleValue: string
        if (props.textParameterPreprocessFn) {
          visibleValue = props.textParameterPreprocessFn(parameter, formattedValue)
        } else {
          visibleValue = formattedValue
        }

        node.textContent = visibleValue
      }

      rafId = requestAnimationFrame(render)
    }

    rafId = requestAnimationFrame(render)

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [
    props.widget.configuration.settings.text,
    props.widget.configuration.settings.color,
    props.widget.configuration.settings.visibility,
    props.widget.configuration.settings.movements,
    props.widget.configuration.settings.rotations,
    loading,
  ])

  return (
    <div
      ref={wrapperRef}
      data-loading={loading}
      style={{
        ...{
          width: '100%',
          height: '100%',
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        ...(props.wrapperStyle ?? {}),
      }}
    >
      {props.widget.id}
    </div>
  )
}

const SVGChart = (props: IProps) => {
  if (!props.widget.configuration.settings.image_id) {
    return <EmptySVGPlaceholder />
  }

  return <SVGChartContent {...props} />
}

export default memo(SVGChart)
